<template>
  <v-app>
    <v-main>
      <router-view />
      <v-dialog v-model="showDialog" persistent max-width="290">
        <v-card class="lgin">
          <v-card-title class="bg-error text-white">
            {{ $t("common.information") }}
          </v-card-title>
          <v-card-text class="message pa-4">{{ message }}</v-card-text>
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn class="btn-success primary-button" @click="closeDialog">
              {{ $t("common.ok") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <v3-loading
      :active="isLoading"
      v-if="isLoading"
      loader="bars"
      color="#54BA57"
      :opacity="0"
      :lock-scroll="true"
      class="loader-on-top"
    ></v3-loading>
  </v-app>
</template>
<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  onBeforeMount,
  inject,
  nextTick,
  watch,
  getCurrentInstance
} from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const axios = inject("axios");
const idleTimeout = ref(null);
const sessionConfiguration = ref(0); // in minutes
const showDialog = ref(false);
const globalMessage = computed(() => {
  if (store.getters && store.getters.global) {
    return store.getters.global.message;
  } else {
    return "No message";
  }
});

const { locale } = useI18n();
const instance = getCurrentInstance();

watch(locale, (newLang) => {
  localStorage.setItem("lang", newLang);

  // ✅ CORRECT way to update Vuetify 3 locale
  instance.appContext.app.config.globalProperties
    .$vuetify.locale.current = newLang;
});

const message = computed(() => {
  if (globalMessage.value !== null) {
    if (globalMessage.value.code) {
      return t(globalMessage.value.code);
    } else {
      return globalMessage.value.message;
    }
  } else {
    return null;
  }
});

const isLoading = computed({
  get() {
    if (store.getters && store.getters.global) {
      return store.getters.global.loading;
    } else {
      return false;
    }
  },
  set(value) {
    store.dispatch("setLoading", {
      loading: value,
    });
  },
});

watch(globalMessage, () => {
  nextTick(() => {
    openDialog();
  });
});

watch(isLoading, () => {
  nextTick(() => {
    if (store.getters && store.getters.global) {
      isLoading.value = store.getters.global.loading;
    } else {
      isLoading.value = false;
    }
  });
});

const openDialog = () => {
  if (store.getters.global.message) showDialog.value = true;
};

const closeDialog = () => {
  store.dispatch("setGlobalMessage", {
    message: null,
  });
  showDialog.value = false;
};

// convert minutes → milliseconds
const timeoutDuration = computed(
  () => Number(sessionConfiguration.value) * 60 * 1000
);

// fetch session timeout setting
const getSessionTimeoutValue = async () => {
  try {
    const response = await axios.get(
      "/vms-api/settings/general/SESSION_TIMEOUT"
    );
    if (response?.status === 200) {
      sessionConfiguration.value = response.data.data.value;
      // console.log("Session timeout (minutes):", sessionConfiguration.value);
    } else {
      console.log("Failed to fetch session timeout:", response);
    }
  } catch (err) {
    console.log("Error fetching session timeout:", err);
  }
};

// logout / clear session
const deleteToken = () => {
  localStorage.removeItem("loginToken");
  // localStorage.clear();
  store.dispatch("setGlobalMessage", {
    message: { code: "altMsgs.sessionExpired" },
  });
  router.push("/login");
  setTimeout(() => {
    showDialog.value = true;
  }, 1000);
  // showDialog.value = true;

  console.log("session Timeout");
};

// start idle timer
const startIdleTimer = () => {
  // console.log("Starting idle timer...", timeoutDuration.value);
  if (timeoutDuration.value > 0) {
    // console.log("Starting idle timer...", timeoutDuration.value);
    idleTimeout.value = setTimeout(deleteToken, timeoutDuration.value);
  }
};

// reset idle timer on activity
const resetIdleTimer = () => {
  // console.log("Resetting idle timer...");
  if (idleTimeout.value) {
    clearTimeout(idleTimeout.value);
  }
  startIdleTimer();
};

// track user activity
const handleActivity = () => resetIdleTimer();

// initialize session timeout
const initializeSession = async () => {
  const token = localStorage.getItem("loginToken");
  console.log("Initializing session...", token);
  if (token) {
    await getSessionTimeoutValue();
    startIdleTimer();
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keypress", handleActivity);
  }
};

// language setup
onBeforeMount(() => {
  if (!localStorage.getItem("lang")) {
    const browserLanguage = navigator.language;
    localStorage.setItem(
      "lang",
      browserLanguage.startsWith("ja") ? "ja" : "en"
    );
  }

  const selectedLang = localStorage.getItem("lang");
  document.body.classList.add(selectedLang === "en" ? "en-lang" : "ja-lang");
});

// mount & unmount
onMounted(() => {
  document.title = "VMS";
  initializeSession();
});

onUnmounted(() => {
  if (idleTimeout.value) {
    clearTimeout(idleTimeout.value);
  }
  window.removeEventListener("mousemove", handleActivity);
  window.removeEventListener("keypress", handleActivity);
});
</script>

<style>
.le-date-picker .v-date-picker-month__day {
  height: 30px;
  width: 30px;
}
.le-date-picker .v-sheet > div:nth-child(1) {
  background: rgb(5, 175, 242) !important;
  background: linear-gradient(
    150deg,
    rgba(5, 175, 242, 1) 50%,
    rgba(242, 217, 141, 1) 90%
  ) !important;
}
.le-date-picker .v-sheet > div .v-date-picker-header {
  background: none !important;
}
.le-date-picker .v-picker-title {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.le-date-picker .v-date-picker-header {
  padding-bottom: 8px !important;
}
.le-date-picker .v-date-picker-header__content {
  font-size: 26px !important;
  line-height: 1 !important;
}
.le-date-picker .v-date-picker {
  width: 290px;
}
.le-date-picker .v-date-picker-month {
  min-width: 260px;
}

.le-date-picker .v-btn--icon.v-btn--density-default,
.le-date-picker .v-date-picker-header {
  height: inherit !important;
}
.le-date-picker .v-picker__header {
  display: none;
}
.le-date-picker .v-date-picker__title {
  font-size: 18px;
}
.tble-icons {
  white-space: nowrap;
}
.grey-bg {
  background: #f5f5f5;
}
.v-input.v-input--density-compact .v-field {
  background-color: #ffffff !important;
}
.v-data-table-header__content {
  font-weight: bold;
}
</style>
